import { useMemo, useState } from 'react';
import type { Setup } from '@/types';
import type { UseTuningSession } from '../hooks/useTuningSession';
import { getTuningSpec } from '../data/bow-tuning-specs';
import { BOWS, bowCategory, type BowCategory } from '@/data/bows';
import { Card } from '@/components/ui/Card';
import { GoalCard, TipCard, WarningCard, InfoCard } from '../components/Cards';
import { Check, AlertCircle, Download } from 'lucide-react';
import { useBowProfiles } from '@/profiles/hooks';
import { bowSetupFromProfile, sightSelectionFromProfile } from '@/profiles/setupBridge';

interface PreflightData {
  /** Hunting vs target — filters the brand/model picker. Defaults to 'hunting'. */
  bowCategory?: BowCategory;
  bowBrand?: string;
  bowModel?: string;
  bowYear?: number;
  /** Measured peak draw weight (lbs). */
  measuredDrawWeight?: number;
  /** Measured draw length (inches). */
  measuredDrawLength?: number;
  /** Measured axle-to-axle (inches). */
  measuredAxleToAxle?: number;
  /** Measured brace height (inches). */
  measuredBraceHeight?: number;
  /** Has the user fired ≥100 arrows since installing new strings? */
  stringsBrokenIn?: boolean;
}

interface PreflightStepProps {
  tuning: UseTuningSession;
  setup: Setup;
  /**
   * Update the active Setup's bow + sight fields. Used by the "Load from My
   * Bows" button to pull a saved BowProfile into the Wizard's Setup so the
   * user doesn't have to re-enter the same bow info between Sketch and Tune.
   */
  onUpdateSetupBow?: (patch: Partial<Setup>) => void;
}

/**
 * Step 1 — Pre-flight & Bow Specs
 *
 * Pulls the bow brand/model/year from the active setup (or lets user pick),
 * fetches the manufacturer tuning spec, and asks the user to measure the
 * current state of their bow. Each measurement is compared against spec
 * with a green/amber/red badge — out-of-spec triggers a contextual fix tip.
 */
export function PreflightStep({ tuning, setup, onUpdateSetupBow }: PreflightStepProps) {
  const data = (tuning.session.stepData['preflight'] as PreflightData | undefined) ?? {};
  const update = (patch: Partial<PreflightData>) =>
    tuning.updateStepData<PreflightData>('preflight', { ...data, ...patch });

  /* Profile bridge — surface any saved bow profiles so the user can load
   * gear they've already entered on /bows.html. The "Load from My Bows"
   * button writes to the Setup's bow field (not to step data) so it carries
   * across every step of the Wizard. */
  const { profiles: bowProfiles, primary: primaryProfile } = useBowProfiles();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [loadedFlash, setLoadedFlash] = useState<string | null>(null);

  const loadFromProfile = (profileId: string) => {
    const profile = bowProfiles.find((p) => p.profileId === profileId);
    if (!profile || !onUpdateSetupBow) return;
    onUpdateSetupBow({
      bow: bowSetupFromProfile(profile),
      sight: sightSelectionFromProfile(profile),
    });
    /* Clear any manual brand/model/year overrides on this step so the new
     * bow surfaces in the picker without stale data overriding it. */
    update({ bowBrand: undefined, bowModel: undefined, bowYear: undefined });
    setPickerOpen(false);
    setLoadedFlash(profile.name);
    window.setTimeout(() => setLoadedFlash(null), 2200);
  };

  // Auto-populate from the Builder's bow setup. Priority order:
  //   1. Anything the user explicitly entered here (data.bow*)
  //   2. The brand/model/year saved on setup.bow (set in BowSetupCard on the Builder)
  //   3. Fallback: fuzzy-match any free-text brand/model on the setup
  const setupMatch = bowMatchFromSetup(setup);
  const bowBrand = data.bowBrand ?? setup.bow.brand ?? setupMatch?.brand;
  const bowModel = data.bowModel ?? setup.bow.model ?? setupMatch?.model;
  const bowYear = data.bowYear ?? setup.bow.year ?? setupMatch?.year;

  const tuningSpec = useMemo(
    () => (bowBrand && bowModel && bowYear ? getTuningSpec(bowBrand, bowModel, bowYear) : null),
    [bowBrand, bowModel, bowYear],
  );

  const bowEntry = useMemo(
    () => BOWS.find((b) => b.brand === bowBrand && b.model === bowModel && b.year === bowYear),
    [bowBrand, bowModel, bowYear],
  );

  // Hunting/Target toggle — defaults to whatever the Builder set, else 'hunting'.
  // User can flip here too without breaking the Builder's selection.
  const category: BowCategory = data.bowCategory ?? setup.bow.category ?? 'hunting';

  // Brands scoped by the active category — so picking "Target" instantly
  // narrows the brand list to those who actually make target bows.
  const scopedBows = useMemo(
    () => BOWS.filter((b) => bowCategory(b) === category),
    [category],
  );
  const brands = useMemo(
    () => Array.from(new Set(scopedBows.map((b) => b.brand))).sort(),
    [scopedBows],
  );

  // Models for the picked brand within the active category.
  const modelsForBrand = useMemo(
    () => scopedBows.filter((b) => b.brand === bowBrand).sort((a, b) => b.year - a.year),
    [scopedBows, bowBrand],
  );

  return (
    <>
      <GoalCard title="What we're trying to achieve">
        Confirm the bow you're tuning matches its manufacturer spec sheet. We need a clean
        baseline before we touch anything else — measurements that drift later will tell us
        what needs adjusting.
      </GoalCard>

      <Card padding="lg">
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <h3 className="font-semibold">Your bow</h3>
          {onUpdateSetupBow && bowProfiles.length > 0 && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setPickerOpen((v) => !v)}
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-md border border-zinc-700 hover:border-ember-500 hover:text-ember-500 transition text-zinc-300"
                title="Load brand/model/specs from a bow you saved on My Bows"
              >
                <Download size={12} />
                Load from My Bows
              </button>
              {pickerOpen && (
                <div className="absolute right-0 mt-1 z-20 w-64 bg-zinc-950 border border-zinc-800 rounded-md shadow-lg p-1.5">
                  <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 px-2 py-1">
                    Saved bow profiles
                  </p>
                  {bowProfiles.map((p) => (
                    <button
                      key={p.profileId}
                      type="button"
                      onClick={() => loadFromProfile(p.profileId)}
                      className="w-full text-left px-2 py-1.5 rounded hover:bg-zinc-900 text-sm"
                    >
                      <span className="block truncate font-medium">{p.name}</span>
                      <span className="block text-[10px] text-zinc-500">
                        {[p.bow.brand, p.bow.model].filter(Boolean).join(' ') || '—'}
                        {' · '}
                        {p.bow.drawWeightLb} lb / {p.bow.drawLengthIn}"
                        {p.isPrimary ? ' · primary' : ''}
                      </span>
                    </button>
                  ))}
                  <a
                    href="/bows.html"
                    className="block px-2 py-1.5 text-[11px] text-zinc-500 hover:text-ember-500 border-t border-zinc-800 mt-1 pt-2"
                  >
                    Manage saved bows →
                  </a>
                </div>
              )}
            </div>
          )}
          {onUpdateSetupBow && bowProfiles.length === 0 && (
            <a
              href="/bows.html"
              className="text-[11px] text-zinc-500 hover:text-ember-500"
              title="Save your bow on /bows.html so you don't have to re-enter it here"
            >
              Save your bow →
            </a>
          )}
        </div>
        {loadedFlash && (
          <p className="text-xs text-emerald-400 mb-3 flex items-center gap-1.5">
            <Check size={12} />
            Loaded <strong className="font-semibold">{loadedFlash}</strong> — specs populated below.
          </p>
        )}
        {primaryProfile && setup.bow.brand !== primaryProfile.bow.brand && bowProfiles.length > 0 && (
          <p className="text-[11px] text-amber-400 mb-3 leading-snug">
            <AlertCircle size={11} className="inline mb-0.5 mr-1" />
            Your primary bow is <strong>{primaryProfile.name}</strong>. This setup is using a different bow — load it above or pick manually.
          </p>
        )}

        {/* ───── Hunting / Target toggle — ALWAYS visible at top ───── */}
        <div className="mb-4">
          <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1.5">
            Bow type
          </p>
          <div className="grid grid-cols-2 gap-0 rounded-md overflow-hidden border border-zinc-800">
            <button
              type="button"
              onClick={() =>
                update({
                  bowCategory: 'hunting',
                  bowBrand: undefined,
                  bowModel: undefined,
                  bowYear: undefined,
                })
              }
              className={
                'px-4 py-2.5 text-sm font-semibold transition ' +
                (category === 'hunting'
                  ? 'bg-ember-500 text-zinc-950'
                  : 'bg-zinc-950 text-zinc-400 hover:text-zinc-200')
              }
            >
              Hunting
            </button>
            <button
              type="button"
              onClick={() =>
                update({
                  bowCategory: 'target',
                  bowBrand: undefined,
                  bowModel: undefined,
                  bowYear: undefined,
                })
              }
              className={
                'px-4 py-2.5 text-sm font-semibold transition border-l border-zinc-800 ' +
                (category === 'target'
                  ? 'bg-sky-500 text-zinc-950'
                  : 'bg-zinc-950 text-zinc-400 hover:text-zinc-200')
              }
            >
              Target
            </button>
          </div>
          <p className="text-[10px] text-zinc-500 mt-1.5 leading-snug">
            {category === 'hunting'
              ? 'Compact ATA hunting compounds — max speed, hunting weights.'
              : 'Long-ATA target compounds (34-40") — smoother draw, lower let-off, target competition.'}
          </p>
        </div>

        {/* ───── Brand + Model + Year (filtered by toggle) ───── */}
        <div className="grid sm:grid-cols-3 gap-3">
          <Field label="Brand">
            <select
              value={bowBrand ?? ''}
              onChange={(e) =>
                update({
                  bowBrand: e.target.value || undefined,
                  bowModel: undefined,
                  bowYear: undefined,
                })
              }
              className={selectClass}
            >
              <option value="">Select brand…</option>
              {brands.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </Field>
          <Field label={`Model${bowBrand ? ` (${modelsForBrand.length})` : ''}`}>
            <select
              value={bowModel && bowYear ? `${bowModel}|${bowYear}` : ''}
              onChange={(e) => {
                const v = e.target.value;
                if (!v) {
                  update({ bowModel: undefined, bowYear: undefined });
                } else {
                  const [m, y] = v.split('|');
                  update({ bowModel: m, bowYear: parseInt(y!, 10) });
                }
              }}
              disabled={!bowBrand}
              className={selectClass}
            >
              <option value="">Select model…</option>
              {modelsForBrand.map((m) => (
                <option key={`${m.model}-${m.year}`} value={`${m.model}|${m.year}`}>
                  {m.model} ({m.year})
                </option>
              ))}
            </select>
          </Field>
          <Field label="Year (override)">
            <input
              type="number"
              value={bowYear ?? ''}
              onChange={(e) => update({ bowYear: parseInt(e.target.value, 10) || undefined })}
              className={inputClass}
              placeholder="2026"
            />
          </Field>
        </div>

        {bowBrand && bowModel && (
          <div className="mt-4">
            {tuningSpec ? (
              <div className="flex items-center gap-2 text-emerald-400 text-xs">
                <Check size={14} />
                <span>Full tuning spec found for {bowBrand} {bowModel} {bowYear}.</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-amber-400 text-xs">
                <AlertCircle size={14} />
                <span>
                  No detailed tuning spec in our database for {bowBrand} {bowModel} {bowYear}.
                  We'll use generic ranges + you can enter manufacturer specs by hand.
                </span>
              </div>
            )}
            {tuningSpec?.notes && (
              <p className="text-[12px] text-zinc-400 leading-relaxed mt-2 italic">
                {tuningSpec.notes}
              </p>
            )}
          </div>
        )}
      </Card>

      {/* Specs at a glance — quick visual reference for what we're checking */}
      {bowEntry && (
        <Card padding="lg">
          <div className="flex items-baseline justify-between mb-3">
            <h3 className="font-semibold">Specs at a glance</h3>
            <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
              {bowEntry.brand} {bowEntry.model} {bowEntry.year}
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <SpecCell label="IBO speed" value={`${bowEntry.iboSpeed} fps`} />
            <SpecCell label="Axle-to-axle" value={bowEntry.axleToAxle ? `${bowEntry.axleToAxle}"` : '—'} />
            <SpecCell label="Brace height" value={bowEntry.brace ? `${bowEntry.brace}"` : '—'} />
            <SpecCell label="Let-off" value={`${bowEntry.letOff}%`} />
            <SpecCell label="Bow weight" value={bowEntry.weightLb ? `${bowEntry.weightLb} lb` : '—'} />
            <SpecCell
              label="Draw weight"
              value={bowEntry.drawWeightRange ? `${bowEntry.drawWeightRange[0]}-${bowEntry.drawWeightRange[1]} lb` : '—'}
            />
            <SpecCell
              label="Draw length"
              value={bowEntry.drawLengthRange ? `${bowEntry.drawLengthRange[0]}"-${bowEntry.drawLengthRange[1]}"` : '—'}
            />
            <SpecCell
              label="Stock center shot"
              value={tuningSpec ? formatFrac(tuningSpec.stockCenterShot) : `13/16"`}
            />
            <SpecCell
              label="Tiller target"
              value={tuningSpec ? `${tuningSpec.tillerTarget === 0 ? '0 (matched)' : `${tuningSpec.tillerTarget}"`}` : '0 (matched)'}
            />
            <SpecCell
              label="Cam type"
              value={tuningSpec ? formatCamArch(tuningSpec.camArchitecture) : '—'}
            />
            <SpecCell
              label="Draw length adj."
              value={tuningSpec ? formatDLSystem(tuningSpec.drawLengthSystem) : '—'}
            />
            <SpecCell
              label="Cable system"
              value={tuningSpec ? formatCableSystem(tuningSpec.cableSystem) : '—'}
            />
          </div>
          <p className="text-[11px] text-zinc-500 mt-3 leading-snug">
            These are the targets your bow should be at. We'll compare your measurements to
            each one as we walk through the steps.
          </p>
        </Card>
      )}

      <Card padding="lg">
        <h3 className="font-semibold mb-1">Your measurements</h3>
        <p className="text-[12px] text-zinc-500 mb-3">
          Measure with the bow at rest, in a press if needed. Compare against
          {bowEntry ? ` the manufacturer spec below` : ' your bow\'s spec sheet'}.
        </p>

        <div className="grid sm:grid-cols-2 gap-3">
          <MeasurementField
            label="Draw weight (lb)"
            value={data.measuredDrawWeight}
            spec={`peak per limb-bolt setting`}
            onChange={(v) => update({ measuredDrawWeight: v })}
            step={1}
            tolerance={undefined}
          />
          <MeasurementField
            label="Draw length (in)"
            value={data.measuredDrawLength}
            spec={`measured wall-to-pivot`}
            onChange={(v) => update({ measuredDrawLength: v })}
            step={0.25}
            tolerance={undefined}
          />
          <MeasurementField
            label="Axle-to-axle (in)"
            value={data.measuredAxleToAxle}
            spec={bowEntry?.axleToAxle ? `${bowEntry.axleToAxle}"` : 'see spec sheet'}
            specValue={bowEntry?.axleToAxle}
            onChange={(v) => update({ measuredAxleToAxle: v })}
            step={0.125}
            tolerance={0.125}
          />
          <MeasurementField
            label="Brace height (in)"
            value={data.measuredBraceHeight}
            spec={bowEntry?.brace ? `${bowEntry.brace}"` : 'see spec sheet'}
            specValue={bowEntry?.brace}
            onChange={(v) => update({ measuredBraceHeight: v })}
            step={0.0625}
            tolerance={0.0625}
          />
        </div>
      </Card>

      {/*
        Strother's peak-weight insight — surfaces ALWAYS in Pre-flight so users
        learn it before they reach for the wrench. Kevin Strother (Strother
        Archery / ex-Bowtech designer) posted this May 2026: the common
        mistake of twisting ONLY cables to add weight front-loads the cam,
        lengthens DL, and SLOWS the bow. Correct fix is twisting string +
        cables in proportion.
      */}
      <InfoCard title="Want a few more pounds of draw weight? Read this first.">
        <p className="mb-2">
          When archers want to bump peak draw weight a few pounds, the instinct is
          to just add twists to the <strong>cables</strong>. Don't. That front-loads
          the cam, lengthens your draw, and the bow ends up <em>slower</em> than
          before — with cam timing off.
        </p>
        <p className="mb-2">
          <strong>Correct method:</strong> twist the <strong>STRING and cables
          equally</strong>. Both sides shorten in proportion → peak weight rises,
          draw length stays put, cam timing stays clean.
        </p>
        <p className="text-[11px] text-zinc-400">
          Practical starting point: 2 twists in each cable + 1 in the string,
          re-measure peak DW and DL, repeat. Re-check cam timing + tiller after.
          (Per Kevin Strother, May 2026.)
        </p>
      </InfoCard>

      {/* Out-of-spec brace height surfaces this troubleshooting card */}
      {bowEntry?.brace && data.measuredBraceHeight !== undefined &&
        Math.abs(data.measuredBraceHeight - bowEntry.brace) > 0.0625 && (
          <TipCard
            title={`Brace height is ${data.measuredBraceHeight > bowEntry.brace ? 'HIGHER' : 'LOWER'} than spec`}
            defaultOpen
          >
            <p className="mb-2">
              <strong>Why it matters:</strong> Brace shifts speed by ~3 fps per 1/8&quot;.
              Sight tapes printed for a different brace height will be off at distance.
            </p>
            <p className="mb-2">
              <strong>Fix:</strong> {data.measuredBraceHeight > bowEntry.brace
                ? 'Untwist your bowstring to LOWER brace height.'
                : 'Add twists to your bowstring to RAISE brace height.'} About
              5-10 twists per 1/16&quot; of brace change. Requires a bow press.
            </p>
            <p className="text-[11px] text-zinc-400">
              ⚠ Bow press required. Always work in small increments and re-measure.
              Re-check draw length (it shifts ~1/8&quot; per 1/16&quot; brace change) and re-time cams.
            </p>
          </TipCard>
        )}

      <Card padding="md">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={!!data.stringsBrokenIn}
            onChange={(e) => update({ stringsBrokenIn: e.target.checked })}
            className="mt-1 w-4 h-4 accent-ember-500"
          />
          <div className="flex-1">
            <p className="text-sm font-medium">My strings have at least 100 arrows on them</p>
            <p className="text-[12px] text-zinc-500 mt-0.5">
              New strings stretch in the first ~100 shots. Tuning before they settle means
              you'll have to re-tune everything in a week. Skip this only if you know your
              strings are broken in.
            </p>
          </div>
        </label>
      </Card>

      {data.stringsBrokenIn === false && (
        <WarningCard title="Tune later, save yourself time" defaultOpen>
          Strings stretch most in their first 100 arrows. Tuning now means you'll be back
          here in a week. Either shoot it in first, or accept that this is a "rough tune"
          you'll revisit.
        </WarningCard>
      )}

      <InfoCard title="What measurements should I take?">
        <p className="mb-2">
          <strong>Draw weight</strong>: hang the bow on a digital scale at full draw, or
          read the limb bolt count if you trust the calibration.
        </p>
        <p className="mb-2">
          <strong>Draw length</strong>: AMO standard = pivot-of-grip to the back of the
          string at full draw, plus 1¾". Easiest: have someone measure with a measuring
          arrow.
        </p>
        <p className="mb-2">
          <strong>Axle-to-axle</strong>: tape measure from cam axle to cam axle, on the
          centerline of the cams.
        </p>
        <p>
          <strong>Brace height</strong>: from the deepest point of the grip (the pivot
          point) straight to the string at brace.
        </p>
      </InfoCard>

      {/* v122 — String Stopper InfoCard. Dudley's quick reference for the
          one bow component that ages silently and degrades everything else
          (string life, group consistency, vibration) without ever telling
          you. Three checks the archer should run before trusting any
          downstream tune result. */}
      <InfoCard title="String stopper · quick reference">
        <p className="mb-2">
          The string stopper is the rubber-tipped post above the grip that catches
          the string just past brace on release. It's a vibration sink and a
          string-life saver, but it perishes silently. Three checks before you
          trust the rest of this tune:
        </p>
        <ol className="list-decimal pl-5 space-y-2 marker:text-ember-400">
          <li>
            <strong>Static gap at brace.</strong> With the bow at brace, the
            rubber tip should clear the string by about 1/16". Touching the
            string at brace pre-loads the limbs slightly differently every time
            you draw — silent variance that shows up as inconsistent groups at
            distance. Too far away and you lose the dampening benefit. Adjust
            the stopper post if your bow allows it; replace with a longer/shorter
            arm if not.
          </li>
          <li>
            <strong>Rubber freshness.</strong> Replace the rubber tip every
            ~2 years even if it looks fine. UV, heat in the truck, and oil from
            handling all kill the elastomer. A hardened tip transmits vibration
            into the string instead of absorbing it — accelerated string wear
            and a buzzier shot.
          </li>
          <li>
            <strong>After-market position.</strong> If you've installed a
            third-party string stopper (Bee Stinger, Trophy Ridge, Limbsaver),
            check that the contact point is centered on the string and the arm
            isn't introducing a side-load. Off-center contact shoves the string
            laterally at release — left/right group walk that no rest
            adjustment will fix.
          </li>
        </ol>
        <p className="mt-3 text-xs text-zinc-500">
          Most pro shops skip the gap check; it's the highest-leverage
          15 seconds you'll spend on this bow.
        </p>
      </InfoCard>

      <WarningCard title="Safety reminder">
        Never adjust limbs, cables, or strings on a strung bow without a press.
        Never dry-fire a compound. When in doubt, take it to a certified pro shop.
      </WarningCard>
    </>
  );
}

// ─── Small UI helpers ───

const selectClass =
  'w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-ember-500 disabled:opacity-50';
const inputClass =
  'w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-ember-500';

function SpecCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-zinc-950/50 border border-zinc-800/60 rounded-md p-2.5">
      <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-0.5">
        {label}
      </p>
      <p className="text-sm font-semibold text-zinc-100">{value}</p>
    </div>
  );
}

function formatFrac(inches: number): string {
  // Convert 0.8125 -> 13/16", 0.75 -> 3/4", etc.
  const sixteenths = Math.round(inches * 16);
  if (sixteenths % 16 === 0) return `${sixteenths / 16}"`;
  // Reduce fraction
  const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));
  const denom = 16;
  const g = gcd(sixteenths, denom);
  return `${sixteenths / g}/${denom / g}"`;
}

function formatCamArch(arch: string): string {
  return {
    'dual-binary': 'Dual binary',
    'hybrid': 'Hybrid',
    'binary': 'Binary',
    'single': 'Single cam',
    'crossover': 'Crossover',
  }[arch] ?? arch;
}

function formatDLSystem(sys: string): string {
  return {
    'rotating-module': 'Rotating module',
    'module-swap': 'Module swap',
    'cam-stop-peg': 'Stop peg',
    'twist-cables': 'Twist cables',
    'micro-adjust-screw': 'Micro screw',
  }[sys] ?? sys;
}

function formatCableSystem(sys: string): string {
  return {
    'yoke-tunable': 'Yoke tunable',
    'shim-tunable': 'Shim tunable',
    'fixed-yoke': 'Fixed yoke',
    'snap-spacer': 'Snap spacer',
    'xts-tunable': 'XTS (no-press, H+V)',
  }[sys] ?? sys;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      {label && <label className="text-[11px] text-zinc-500 block mb-1">{label}</label>}
      {children}
    </div>
  );
}


function MeasurementField({
  label, value, spec, specValue, onChange, step, tolerance,
}: {
  label: string;
  value: number | undefined;
  spec: string;
  specValue?: number;
  onChange: (v: number | undefined) => void;
  step: number;
  tolerance: number | undefined;
}) {
  const badge = useMemo(() => {
    if (value === undefined || specValue === undefined || tolerance === undefined) return null;
    const delta = Math.abs(value - specValue);
    if (delta <= tolerance) return { color: 'emerald', label: '✓ in spec' };
    if (delta <= tolerance * 2) return { color: 'amber', label: '⚠ borderline' };
    return { color: 'rose', label: '✗ out of spec' };
  }, [value, specValue, tolerance]);

  return (
    <div>
      <div className="flex items-baseline justify-between mb-1">
        <label className="text-[11px] text-zinc-500">{label}</label>
        <span className="text-[10px] text-zinc-600 font-mono">spec: {spec}</span>
      </div>
      <div className="flex items-center gap-2">
        <input
          type="number"
          value={value ?? ''}
          step={step}
          onChange={(e) => {
            const v = e.target.value;
            onChange(v === '' ? undefined : parseFloat(v));
          }}
          className={inputClass + ' flex-1'}
          placeholder="—"
        />
        {badge && (
          <span
            className={
              'text-[10px] px-2 py-1 rounded font-mono whitespace-nowrap ' +
              (badge.color === 'emerald'
                ? 'bg-emerald-900/40 text-emerald-300 border border-emerald-700/40'
                : badge.color === 'amber'
                  ? 'bg-amber-900/40 text-amber-300 border border-amber-700/40'
                  : 'bg-rose-900/40 text-rose-300 border border-rose-700/40')
            }
          >
            {badge.label}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Best-effort match of the active setup's free-text bow brand/model to our catalog.
 * Returns null if the setup's bow doesn't appear in BOWS — user picks manually.
 */
function bowMatchFromSetup(setup: Setup): { brand: string; model: string; year: number } | null {
  // Setup.bow has freeform fields; not all map directly. For now, try basic match.
  // Future enhancement: BowSetup should reference a BowModel by id.
  const bow = setup.bow as { brand?: string; model?: string; year?: number };
  if (!bow.brand || !bow.model) return null;
  const found = BOWS.find(
    (b) => b.brand === bow.brand && b.model === bow.model && (!bow.year || b.year === bow.year),
  );
  if (found) return { brand: found.brand, model: found.model, year: found.year };
  return null;
}
